import { type SharedValue, useAnimatedStyle } from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns an animated style that controls the opacity of a single character
 * in a typewriter animation. The character becomes visible (opacity 1) once
 * the animated index passes its position.
 *
 * @returns An animated style with `{ opacity: 0 | 1 }`.
 *
 * @example
 * ```tsx
 * const animatedIndex = useTypewriterAnimation({ text: 'Hello', charDelayMs: 50 });
 *
 * const style = useTypewriterAnimationCharStyle({ index: 2, animatedIndex });
 * // -> { opacity: 0 } until the animation reaches character index 2, then { opacity: 1 }
 *
 * return <Animated.Text style={style}>l</Animated.Text>;
 * ```
 */
export const useTypewriterAnimationCharStyle = (params: Params) => {
  const { index, animatedIndex } = params;

  return useAnimatedStyle(() => {
    const isVisible = animatedIndex.value > index;

    return { opacity: isVisible ? 1 : 0 };
  });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  index: number;
  animatedIndex: SharedValue<number>;
};
